import { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Loader from "../common/Loader.js"
import DateRangeFilter from "./components/DateRangeFilter.js"
import AgentMlDuesBanner from "./components/AgentMlDuesBanner.js"
import { AuthContext } from "../../context/AuthContext.js"
import { hasAttendanceAccess, hasLocationHubBookingAccess } from "../../utils/networkMode.js"
import { DashboardLayout, StatCard, SectionTable, PageHeader } from "../common/page_component/index.js"
import ScanQrStatusButton from "../shipment-qr-status/ScanQrStatusButton.js"
import { Clock } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useOperationsDashboardData } from "./components/useOperationsDashboardData.js"
import OperationsDashboardView from "./components/OperationsDashboardView.js"
import RefreshingBanner from "./components/RefreshingBanner.js"
import DashboardQuickActionButtons from "./components/DashboardQuickActionButtons.js"
import LocationTargetsButton from "./components/LocationTargetsButton.js"
import companySettlementService from "../../services/companySettlementService.js"
import { fullCurrency } from "../../utils/formatCurrency.js"

const ExpenseHeaderCard = ({ label, amount, className, note }) => (
  <div className={`rounded-lg border px-2.5 py-1.5 min-w-[7.5rem] shrink-0 ${className}`}>
    <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wide leading-tight">{label}</p>
    <p className="text-[11px] sm:text-xs font-bold tabular-nums leading-tight mt-0.5">{fullCurrency(amount)}</p>
    {note ? (
      <p className="text-[9px] sm:text-[10px] font-semibold leading-tight text-red-600 mt-0.5">{note}</p>
    ) : null}
  </div>
)

const AgentDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useContext(AuthContext)
  const isInternalNetwork = user?.network_mode !== 'EXTERNAL';
  const dash = useOperationsDashboardData({ includeMlDues: isInternalNetwork })

  const companyAccess = user?.company_features || user?.company_can_access || {};
  const hubBookingAllowed = hasLocationHubBookingAccess(user);
  const attendanceEnabled = hasAttendanceAccess(user);
  const isAttendanceOnly = attendanceEnabled
    && !companyAccess.shipment
    && !companyAccess.commission
    && !companyAccess.external_commission
    && !companyAccess.on_contract
    && !companyAccess.on_contract_truck;

  const mlOutstanding = Number(dash.mlAgentDues?.total_amount_outstanding || 0)
  const [settlement, setSettlement] = useState(null)

  useEffect(() => {
    if (!isInternalNetwork) return undefined
    let active = true
    companySettlementService.getSummary()
      .then((body) => {
        if (active) setSettlement(body?.data || body || null)
      })
      .catch(() => {
        if (active) setSettlement(null)
      })
    return () => {
      active = false
    }
  }, [isInternalNetwork])

  const approvedExpenses = Number(settlement?.branch_expense_credit || 0)
  const pendingExpenses = Number(settlement?.pending_branch_expense || 0)
  const returnAmount = settlement
    ? Math.max(0, Number(settlement.net_settlement || 0))
    : mlOutstanding
  const duesForHeader = dash.mlAgentDues
    ? { ...dash.mlAgentDues, total_amount_outstanding: returnAmount }
    : null

  const headerRight = (
    <>
      {isInternalNetwork && pendingExpenses > 0 && (
        <ExpenseHeaderCard
          label="Pending expenses"
          amount={pendingExpenses}
          className="border-gray-200 bg-white text-gray-800"
        />
      )}
      {isInternalNetwork && (
        <AgentMlDuesBanner
          dues={duesForHeader}
          expenseCredit={approvedExpenses}
          user={user}
          onViewMlSheets={() => navigate("/company-settlements")}
        />
      )}
      <LocationTargetsButton />
      <ScanQrStatusButton mobileIconOnly />
      {!isAttendanceOnly && (
        <DashboardQuickActionButtons hubBookingAllowed={hubBookingAllowed} />
      )}
      <DateRangeFilter
        value={dash.dateRange} onChange={dash.setDateRange}
        customStart={dash.customStart} customEnd={dash.customEnd}
        onCustomStartChange={dash.setCustomStart} onCustomEndChange={dash.setCustomEnd}
      />
    </>
  );

  if (dash.loading && companyAccess.shipment) return (
    <DashboardLayout>
      <Loader fullScreen={false} message={t('dashboard.loading')} />
    </DashboardLayout>
  )

  const ai = dash.attendanceData?.insights || {}

  if (isAttendanceOnly) {
    return (
      <DashboardLayout>
        <PageHeader title={`Welcome, ${user?.name || "Agent"}`}>
          <LocationTargetsButton />
          <ScanQrStatusButton mobileIconOnly />
          <DateRangeFilter
            value={dash.dateRange} onChange={dash.setDateRange}
            customStart={dash.customStart} customEnd={dash.customEnd}
            onCustomStartChange={dash.setCustomStart} onCustomEndChange={dash.setCustomEnd}
          />
        </PageHeader>
        <RefreshingBanner show={dash.refreshing} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <StatCard label={t('dashboard.totalRecords')} value={ai.total_records || 0} icon={<Clock className="w-5 h-5 text-white" />} color="bg-blue-600" />
          <StatCard label={t('dashboard.totalWorkHours')} value={ai.total_work_hours || '0h 0m'} icon={<Clock className="w-5 h-5 text-white" />} color="bg-purple-500" />
          <StatCard label={t('dashboard.avgWorkHours')} value={ai.avg_work_hours || '0h 0m'} icon={<Clock className="w-5 h-5 text-white" />} color="bg-indigo-500" />
        </div>
        <SectionTable title={t('dashboard.recentAttendance')}
          headers={[t('dashboard.user'), t('common.date'), t('dashboard.login'), t('dashboard.logout'), t('dashboard.hours')]}
          rows={(dash.attendanceData?.recent_records || []).map(r => [
            r.user_name, r.attendance_date,
            r.login_time ? new Date(r.login_time).toLocaleTimeString() : '-',
            r.logout_time ? new Date(r.logout_time).toLocaleTimeString() : t('dashboard.activeSession'),
            r.work_hours || '-'
          ])}
          emptyMsg={t('dashboard.noRecentAttendance')}
        />
      </DashboardLayout>
    )
  }

  return (
    <OperationsDashboardView
      title={`Welcome, ${user?.name || "Agent"}`}
      headerRight={headerRight}
      showHubFilter={false}
      hubs={[]}
      hubIds={[]}
      cargoHubIds={[]}
      bookingHubIds={[]}
      shipmentHubIds={[]}
      cargoData={dash.cargoData}
      bookingSummary={dash.bookingSummary}
      bookingMetric={dash.bookingMetric}
      onBookingMetricChange={dash.setBookingMetric}
      onSummaryDateChange={dash.setSummaryDate}
      cargoLoading={dash.cargoLoading}
      bookingLoading={dash.bookingLoading}
      commissionLoading={dash.commissionLoading}
      onContractLoading={dash.onContractLoading}
      tripLoading={dash.tripLoading}
      refreshing={dash.refreshing}
      mainRefreshing={dash.mainRefreshing}
      onContractVehicleId={dash.onContractVehicleId}
      onOnContractVehicleChange={dash.setOnContractVehicleId}
      tripVehicleId={dash.tripVehicleId}
      onTripVehicleChange={dash.setTripVehicleId}
      tripPartyId={dash.tripPartyId}
      onTripPartyChange={dash.setTripPartyId}
      attendanceLoading={dash.attendanceLoading}
      attendanceUserId={dash.attendanceUserId}
      onAttendanceUserChange={dash.setAttendanceUserId}
      attendanceHubId={dash.attendanceHubId}
      onAttendanceHubChange={dash.setAttendanceHubId}
      salaryLoading={dash.salaryLoading}
      salaryUserId={dash.salaryUserId}
      onSalaryUserChange={dash.setSalaryUserId}
      salaryHubId={dash.salaryHubId}
      onSalaryHubChange={dash.setSalaryHubId}
      salaryMonth={dash.salaryMonth}
      onSalaryMonthChange={dash.setSalaryMonth}
      salaryYear={dash.salaryYear}
      onSalaryYearChange={dash.setSalaryYear}
      user={user}
      shipmentData={dash.shipmentData}
      commissionData={dash.commissionData}
      onContractData={dash.onContractData}
      tripContractData={dash.tripContractData}
      attendanceData={dash.attendanceData}
      salaryData={dash.salaryData}
      dateRange={dash.dateRange}
      customStart={dash.customStart}
      customEnd={dash.customEnd}
      mlOutstanding={isInternalNetwork ? returnAmount : null}
      mlAgentDues={isInternalNetwork ? dash.mlAgentDues : null}
      approvedExpenses={isInternalNetwork ? approvedExpenses : 0}
      pendingExpenses={isInternalNetwork ? pendingExpenses : 0}
      attendanceEnabled={attendanceEnabled}
      companyAccess={companyAccess}
    />
  )
}

export default AgentDashboard;
